<!-- ENDURANCE — the headline feature.

     No levels. No end. Questions keep coming, faster and harder,
     drawn from every mode, until he finally cannot keep up.

     THE SCORE IS A TIME. Points are still counted and still feed the shared
     total, but the number he is chasing is how long he lasted, which is what
     makes it read as a personal best rather than a high score.

     Three lives, and running the clock down spends one just as a wrong answer
     does, so there is no waiting it out. Jeremy asked for lives here too,
     which is why this mode has them despite the original brief. -->
<template>
  <IonPage>
    <AppHeader title="Endurance" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <AppLargeTitle title="Endurance" />

        <div class="pad">
          <p class="ex-sub" style="margin-top:0">
            Questions from every mode, coming faster and harder until you drop.
            Three lives, then it is over. The only score is how long you
            lasted.
          </p>
        </div>

        <div class="ex-total" style="margin-top: var(--sp-5)">
          <div>
            <div class="k">Your record</div>
            <div class="n" :class="{ tabular: best > 0 }" :style="best ? null : 'font-size: var(--text-lg)'">
              {{ best ? spellDuration(best) : 'Not yet' }}
            </div>
          </div>
        </div>

        <IonList inset class="list-inset">
          <IonListHeader>
            <IonLabel>
              <h3>How it wears you down</h3>
              <p>Every stage gives you less time to think.</p>
            </IonLabel>
          </IonListHeader>
          <IonItem v-for="s in ENDURANCE_STAGES" :key="s.label" lines="full">
            <IonLabel>
              <h3>{{ s.label }}</h3>
              <p>{{ (s.perQuestionMs / 1000).toFixed(1) }} seconds a question</p>
            </IonLabel>
            <IonNote slot="end">{{ stageFrom(s) }}</IonNote>
          </IonItem>
        </IonList>

        <div class="pad" style="margin-top: var(--sp-4)">
          <IonButton expand="block" size="large" color="danger" @click="begin">
            Start the run
          </IonButton>
        </div>
        <div class="tail" />
      </template>

      <template v-else-if="current">
        <div ref="stageRef" tabindex="-1">
          <GameHud :score="score" :streak="streak" label="Lasted" :value="shortDuration(watch_.elapsed.value)" :lives="lives" />

          <p class="center ex-eyebrow" style="margin: 0">{{ stage.label }}</p>
          <div class="clock-bar" :class="clockClass" aria-hidden="true"><i :style="{ width: clockPct + '%' }" /></div>

          <!-- `locked`, not `revealed`, during the countdown. Folding the two
               together painted the correct answer green while 3-2-1 was still on
               screen — hidden only because that overlay happens to be opaque. -->
          <QuickfireBoard
            :question="current"
            :revealed="revealed"
            :locked="counting"
            :chosen="chosen"
            @answer="answer"
          />
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <StartCountdown :when="counting" label="How long can you last?" @go="runIt" />
    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonButton, IonList, IonListHeader, IonItem, IonLabel, IonNote,
} from '@ionic/vue';
import { AppHeader, AppLargeTitle, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import QuickfireBoard from '../components/QuickfireBoard.vue';
import StartCountdown from '../components/StartCountdown.vue';
import {
  pointsFor, enduranceStage, ENDURANCE_STAGES, spellDuration, shortDuration,
} from '../game/engine.js';
import { drawQuickfire } from '../game/quickfire.js';
import { profile, ensureProfile, drawOptions, difficultyFloor } from '../game/profile.js';
import { useCountdown, useStopwatch } from '../game/clock.js';
import { finishRun } from '../game/run.js';
import { useAbandonGuard, useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { regainLife } from '../game/lives.js';
import { haptic } from '../core/util.js';

const router = useRouter();

const started = ref(false);
const counting = ref(false);
const over = ref(false);
const lives = ref(3);
const queue = ref([]);
const idx = ref(0);
const revealed = ref(false);
const chosen = ref(-1);
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const verdict = ref({ text: '', why: '', good: true });

const best = computed(() => profile.value.bestEnduranceMs ?? 0);

// `watch_` rather than `watch`: the plain name shadows Vue's own `watch`
// import in this file, and the resulting error names neither.
const watch_ = useStopwatch();
const stage = computed(() => enduranceStage(watch_.elapsed.value, difficultyFloor.value));

const clock = useCountdown(ENDURANCE_STAGES[0].perQuestionMs, () => missed());
const current = computed(() => queue.value[idx.value] ?? null);
const clockPct = computed(() => (clock.remaining.value / stage.value.perQuestionMs) * 100);
const clockClass = computed(() => (clockPct.value <= 25 ? 'panic' : clockPct.value <= 50 ? 'warn' : ''));

const stageFrom = (s) => {
  const i = ENDURANCE_STAGES.indexOf(s);
  const from = i === 0 ? 0 : ENDURANCE_STAGES[i - 1].untilMs;
  return from === 0 ? 'from the start' : `after ${Math.round(from / 60000)} min`;
};

// THIS SCREEN IS A TAB ROOT, which is exactly why the guard matters most here.
// There is no back button to be careful with — just three other tabs one tap
// away along the bottom edge, where a thumb already rests. Losing a four-minute
// personal best that way would be the worst thing the app could do to him.
useAbandonGuard(
  computed(() => started.value && !over.value),
  {
    header: 'Give up the run?',
    message: 'Your time is still counting. Leaving now loses it.',
    confirmText: 'Give up',
    cancelText: 'Keep going',
    // Confirming used only to PERMIT the navigation — the run itself carried
    // on in the background, spending a life per timeout. "Give up" now gives up.
    onAbandon: () => teardown(),
  },
);

let advanceTimer = null;

function begin() {
  // Drawn at the floor he chose, but Endurance climbs past it regardless — the
  // stage decides the difficulty from here, not the settings screen.
  queue.value = drawQuickfire({ ...drawOptions.value, count: 120 });
  started.value = true;
  counting.value = true;
}

function runIt() {
  counting.value = false;
  watch_.reset();
  watch_.start();
  clock.reset(stage.value.perQuestionMs);
  clock.start();
  // The Start button and the 3-2-1 overlay are both gone by now, so without
  // this a keyboard or screen-reader user is left on <body> for the whole run.
  nextTick(() => stageRef.value?.focus());
}

function answer(i) {
  if (revealed.value || over.value || counting.value || !current.value) return;
  clock.stop();
  const q = current.value;
  revealed.value = true;
  chosen.value = i;

  if (i === q.answer) {
    if (q.aus) { ausCount.value += 1; ausNow.value = true; }
    score.value += pointsFor({ difficulty: q.difficulty, streak: streak.value, aus: q.aus });
    streak.value += 1;
    regainLife(lives);
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = { text: streak.value >= 3 ? pun('streak') : pun('correct'), why: '', good: true };
    clearTimeout(advanceTimer);
    // Short, and it gets shorter as the stages climb: the pause between
    // questions is part of what makes late Endurance feel relentless.
    advanceTimer = setTimeout(next, q.aus ? 260 : Math.max(260, stage.value.perQuestionMs / 14));
  } else {
    loseALife(q.reason, pun('wrong'));
  }
}

function missed() {
  if (revealed.value || over.value) return;
  revealed.value = true;
  chosen.value = -1;
  loseALife(current.value?.reason ?? '', pun('clock'), 'warning');
}

// Three lives here too. The score is still a TIME, so a life is really three
// chances to keep the clock running rather than three chances at points.
function loseALife(why, text, feel = 'error') {
  wrong.value += 1;
  streak.value = 0;
  lives.value -= 1;
  haptic(feel);
  verdict.value = { text, why, good: false };
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(() => (lives.value <= 0 ? end() : next()), 1800);
}

function next() {
  if (over.value) return;
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = -1;
  idx.value += 1;
  if (idx.value >= queue.value.length) {
    queue.value = [...queue.value, ...drawQuickfire({ ...drawOptions.value, count: 80 })];
  }
  clock.reset(stage.value.perQuestionMs);
  clock.start();
  // The pace only gets faster from here, and it's the only way a screen
  // reader finds out a new prompt has replaced the last one.
  if (current.value) announce(current.value.prompt);
}

async function end() {
  if (over.value) return;
  over.value = true;
  clock.stop();
  watch_.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'endurance',
    score: score.value,
    lastedMs: watch_.elapsed.value,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
  });
  router.replace('/tabs/endurance/results');
}

onMounted(ensureProfile);
// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  over.value = true;
  clock.stop();
  watch_.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  over.value = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  counting.value = false;
  over.value = false;
  lives.value = 3;
  queue.value = [];
  idx.value = 0;
  revealed.value = false;
  chosen.value = -1;
  ausNow.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  ausCount.value = 0;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => { over.value = true; clearTimeout(advanceTimer); });
</script>
